import React from "react";
import { Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { cellText } from "./gridUtils";
import { normalizeScratchpadRow, scratchpadColumnsFor } from "./scratchpadModel";

export const ScratchpadMobileGrid = ({ section, rows, onRowsChange, readOnly, selectedIds, onToggleRow }) => {
  const columns = scratchpadColumnsFor(section);
  const update = (rowIndex, column, value) => {
    const next = rows.slice();
    const changed = { ...next[rowIndex], [column.key]: column.type === "number" ? Number(value) || 0 : value };
    next[rowIndex] = normalizeScratchpadRow(column.transformRow ? column.transformRow(changed, value, next[rowIndex]) : changed);
    onRowsChange(next);
  };
  const remove = (rowIndex) => onRowsChange(rows.filter((_, index) => index !== rowIndex));

  if (!rows.length) return <div className="rounded-md border border-dashed border-slate-300 px-4 py-10 text-center text-sm text-slate-400" data-testid={`mobile-grid-empty-${section}`}>No rows yet — add a blank row to begin.</div>;

  return (
    <div className="space-y-3" data-testid={`mobile-grid-${section}`}>
      {rows.map((row, rowIndex) => (
        <div key={row.row_id} className="rounded-md border border-slate-200 bg-white p-3 shadow-sm" data-testid={`mobile-row-${section}-${rowIndex}`}>
          <div className="mb-2 flex items-center justify-between border-b border-slate-100 pb-2">
            <label className="flex items-center gap-2 font-mono text-xs font-semibold text-slate-500">
              <Checkbox checked={selectedIds?.has(row.row_id)} onCheckedChange={checked => onToggleRow?.(row.row_id, checked === true)} disabled={readOnly} data-testid={`mobile-row-select-${section}-${rowIndex}`} />
              ROW {rowIndex + 1}
            </label>
            {!readOnly && <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => remove(rowIndex)} data-testid={`mobile-delete-row-${section}-${rowIndex}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {columns.map(column => (
              <label key={column.key} className={`${["description", "notes"].includes(column.key) ? "col-span-2" : ""} min-w-0 text-[11px] font-semibold text-slate-500`}>
                {column.label}
                {column.type === "computed" ? (
                  <div className="mt-1 flex h-9 w-full items-center justify-end rounded-md border bg-slate-50 px-2 font-mono text-sm text-slate-700" data-testid={`mobile-cell-${section}-${rowIndex}-${column.key}`}>{cellText(column, row)}</div>
                ) : column.type === "select" ? (
                  <select className="mt-1 h-9 w-full rounded-md border bg-white px-2 text-sm font-normal text-slate-800" value={row[column.key] ?? ""} onChange={event => update(rowIndex, column, event.target.value)} disabled={readOnly || Boolean(column.readOnly?.(row))} data-testid={`mobile-cell-${section}-${rowIndex}-${column.key}`}>
                    {(column.optionsFor ? column.optionsFor(row) : column.options).map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                ) : (
                  <Input className="mt-1 h-9 text-sm font-normal text-slate-800" inputMode={column.type === "number" ? "decimal" : "text"} value={row[column.key] ?? ""} onChange={event => update(rowIndex, column, event.target.value)} disabled={readOnly || Boolean(column.readOnly?.(row))} data-testid={`mobile-cell-${section}-${rowIndex}-${column.key}`} />
                )}
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};